/**
 * 工作区页面布局模式切换：重排坐标 + 切换后视口适应（见 docs/工作区/布局.md）
 */
import type { Page } from '@/components/canvas/document/publicTypes';
import type { FitToScreenOptions } from '@/components/canvas/store/slices/viewport.slice';
import { useCanvasStore } from '@/components/canvas/store/canvas.store';
import { relayoutPagesForVerticalZoom } from '@/components/canvas/workspace/model/pageArrangement';
import type { WorkspacePageLayoutMode } from '@/components/canvas/workspace/model/workspacePageLayoutMode';
import {
  hasWorkspacePageFitRunner,
  requestFitCurrentPage,
} from '@/components/canvas/workspace/viewport/workspacePageFitController';

export type LayoutSwitchFitTarget = {
  pageId: string;
  /** 无激活页时需将 currentPageId 设为第一页 */
  shouldActivateFirstPage: boolean;
};

/**
 * 布局切换后的适应目标。
 * - 有当前激活页 → 适应该页
 * - 无激活页 → 激活第一页并适应
 */
export function resolveLayoutSwitchFitTarget(
  pages: Page[],
  currentPageId: string,
): LayoutSwitchFitTarget {
  if (pages.length === 0) {
    return { pageId: '', shouldActivateFirstPage: false };
  }
  if (currentPageId && pages.some((p) => p.id === currentPageId)) {
    return { pageId: currentPageId, shouldActivateFirstPage: false };
  }
  return { pageId: pages[0]!.id, shouldActivateFirstPage: true };
}

type FitToScreenFn = (
  containerWidth: number,
  containerHeight: number,
  options?: FitToScreenOptions,
) => void;

function queryWorkspaceShellSize(): { width: number; height: number } | null {
  const el = document.querySelector('.workspace') as HTMLElement | null;
  if (!el || el.clientWidth <= 0 || el.clientHeight <= 0) return null;
  return { width: el.clientWidth, height: el.clientHeight };
}

function runFitCurrentPageWithVerticalReflow(fitToScreen: FitToScreenFn, layoutMode: WorkspacePageLayoutMode) {
  const size = queryWorkspaceShellSize();
  if (!size) return;

  const runFit = () => {
    fitToScreen(size.width, size.height, { chromeInsets: 'edit' });
  };

  runFit();

  if (layoutMode !== 'vertical') return;
  const st = useCanvasStore.getState();
  if (st.pages.length === 0) return;
  const nextPages = relayoutPagesForVerticalZoom(st.pages, st.zoom);
  const yChanged = nextPages.some(
    (p, i) => p.workspaceLayout.y !== st.pages[i]?.workspaceLayout.y,
  );
  if (yChanged) {
    useCanvasStore.setState({ pages: nextPages });
    runFit();
  }
}

export type ScheduleFitCurrentWorkspacePageOptions = {
  /** 横向/纵向翻页等与演示态一致的视口平移缩放动画 */
  animated?: boolean;
};

/**
 * 切换激活页或翻页后，对当前激活页执行 `fitToScreen`（编辑视口，含页头留白）。
 * 纵向模式在首次 fit 改 zoom 后需按新 zoom 重排页顶 y 再 fit 一次（与布局切换一致）。
 */
export function scheduleFitCurrentWorkspacePage(
  options: ScheduleFitCurrentWorkspacePageOptions = {},
): void {
  if (hasWorkspacePageFitRunner()) {
    requestFitCurrentPage(options);
    return;
  }
  requestAnimationFrame(() => {
    const st = useCanvasStore.getState();
    runFitCurrentPageWithVerticalReflow(st.fitToScreen, st.workspacePageLayoutMode);
  });
}

/**
 * 布局重排并更新 store 后，对当前激活页执行 `fitToScreen`。
 * 纵向模式在首次 fit 改 zoom 后需按新 zoom 重排页顶 y 再 fit 一次（与首次进入一致）。
 */
export function scheduleFitAfterLayoutModeSwitch(
  fitToScreen: FitToScreenFn,
  layoutMode: WorkspacePageLayoutMode,
): void {
  requestAnimationFrame(() => {
    runFitCurrentPageWithVerticalReflow(fitToScreen, layoutMode);
  });
}
